const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

function getUserEmail() {
  try {
    const u = JSON.parse(localStorage.getItem('vroum_user') || '{}');
    return u.email || null;
  } catch { return null; }
}

// Core reporter — all other functions go through here
async function report(type, message, extra = {}) {
  try {
    const body = JSON.stringify({
      type,
      message: String(message).slice(0, 500),
      url: window.location.href,
      user_email: getUserEmail(),
      user_agent: navigator.userAgent,
      extra,
    });
    // sendBeacon for page-unload events (404s on navigation etc.), fetch otherwise
    if (type === 'not_found' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(`${API}/debug-report`, blob);
    } else {
      await fetch(`${API}/debug-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
    }
  } catch { /* never throw from reporter */ }
}

// ─── Public API ──────────────────────────────────────────────────────────────

export const reportError = (message, extra = {}) => report('error', message, extra);
export const reportWarning = (message, extra = {}) => report('warning', message, extra);

/**
 * Report a 404 / not-found page.
 * Call in GarageDetailPage, SearchPage, etc. when the API returns 404.
 */
export function reportNotFound(path, extra = {}) {
  return report('not_found', `404: ${path}`, { path, ...extra });
}

/**
 * Report a search that returned 0 results.
 * Helps identify unserved queries (missing garages, wrong city names, etc.)
 */
export function reportZeroResults(query, extra = {}) {
  // Throttle: only report once per session per unique query to avoid spam
  const key = `zero_results:${query}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore quota/security errors */ }
  return report('zero_results', `0 results for: "${query}"`, { query, ...extra });
}

/**
 * Report a slow API call (>threshold ms) OR any non-2xx response.
 * Wrap around fetch() calls that are user-visible.
 */
export async function timedFetch(url, opts, thresholdMs = 3000) {
  const start = Date.now();
  const cleanUrl = url.replace(/\?.*/, ''); // strip query params (may contain PII)
  try {
    const response = await fetch(url, opts);
    const elapsed = Date.now() - start;
    if (elapsed > thresholdMs) {
      report('slow_api', `Slow API: ${elapsed}ms — ${cleanUrl}`, {
        url: cleanUrl,
        elapsed_ms: elapsed,
        threshold_ms: thresholdMs,
        status: response.status,
      });
    }
    // 4xx/5xx that aren't 404 (404 has its own reporter via reportNotFound)
    if (response.status >= 500 || (response.status >= 400 && response.status !== 404 && response.status !== 401)) {
      const key = `api_error:${response.status}:${cleanUrl}`;
      try {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          report('api_error', `API ${response.status} — ${cleanUrl}`, {
            url: cleanUrl,
            status: response.status,
            method: (opts && opts.method) || 'GET',
          });
        }
      } catch { /* ignore */ }
    }
    return response;
  } catch (err) {
    const elapsed = Date.now() - start;
    // Silent on disconnections — these aren't bugs, the request never reached the server.
    // Triggers: Googlebot bails, React unmounts mid-fetch, mobile drops, CSP/CORS block,
    //          iOS Safari "Load failed" on backgrounded tabs, DNS failure, network down.
    const msg = err.message || '';
    const isAbort = err.name === 'AbortError' || /abort|cancell?ed/i.test(msg);
    const isNetwork = /load failed|failed to fetch|networkerror|err_/i.test(msg);
    // Anything failing in <50ms didn't reach the server — it's a browser-level reject.
    if (isAbort || isNetwork || elapsed < 50) {
      throw err;
    }
    report('slow_api', `API failed after ${elapsed}ms — ${cleanUrl}`, {
      url: cleanUrl,
      elapsed_ms: elapsed,
      threshold_ms: thresholdMs,
      error: err.message,
    });
    throw err;
  }
}

/**
 * Report a broken image (usually garage photos / CDN 404s).
 * Fired from a global <img onerror> listener installed below.
 */
export function reportBrokenImage(src, extra = {}) {
  const key = `broken_image:${src}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return report('broken_image', `Broken image: ${src.slice(0, 120)}`, { src, ...extra });
}

/**
 * Report a rage-click: user clicked the same element 3+ times in 2s.
 * Strong UX frustration signal — likely dead button, slow handler, or bug.
 */
export function reportRageClick(selector, count, extra = {}) {
  return report('rage_click', `Rage click (${count}×) on ${selector}`, { selector, count, ...extra });
}

/**
 * Report a failed payment / Stripe checkout error.
 * Revenue-critical — fires on any Stripe error shown to user.
 */
export function reportPaymentFailed(stage, reason, extra = {}) {
  return report('payment_failed', `Payment failed (${stage}): ${reason}`, {
    stage, reason, ...extra,
  });
}

/**
 * Report a landing page (brand/city) that rendered with 0 garages.
 * SEO-critical: these pages are in the sitemap; empty = indexable void.
 */
export function reportEmptyLandingPage(pageType, slug, extra = {}) {
  const key = `empty_landing:${pageType}:${slug}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return report('empty_landing_page', `Empty ${pageType} page: ${slug}`, {
    page_type: pageType, slug, ...extra,
  });
}

/**
 * Report that the user denied / failed geolocation on a "near me" flow.
 * Search-quality signal for Emergency page + near-me search.
 */
export function reportGeoDenied(context, reason, extra = {}) {
  return report('geo_denied', `Geolocation denied (${context}): ${reason}`, {
    context, reason, ...extra,
  });
}

/**
 * Report a failed offer claim.
 */
export function reportOfferClaimFailed(offerId, reason, extra = {}) {
  return report('offer_claim_failed', `Offer claim failed: ${offerId} — ${reason}`, {
    offer_id: offerId,
    reason,
    ...extra,
  });
}

/**
 * Report a failed auth attempt (login/register errors visible to user).
 * Already fired server-side for wrong credentials — this catches client-side
 * timeouts and network errors that never reach the server.
 */
export function reportAuthFailed(mode, reason, extra = {}) {
  return report('auth_failed', `Auth failed (${mode}): ${reason}`, {
    mode,
    reason,
    ...extra,
  });
}

/**
 * Report a main-thread long task (>500ms blocking).
 * Heavy render cycles, sync JSON parses, unbroken loops — stuff that
 * freezes scroll/interaction on mid-range phones.
 */
/**
 * Report a user-submitted form that the API rejected (4xx/5xx).
 *
 * 2026-05-06: added because the Wave 47 newsletter modal had 28d of users
 * trying to submit and getting silent server failures — we never knew.
 * Wrap form submit handlers like:
 *
 *   const r = await fetch(`${API}/...`, ...);
 *   if (!r.ok) {
 *     reportSubmissionError('newsletter_capture', r.status, await r.text());
 *     setError('...');
 *     return;
 *   }
 *
 * Throttled per (form_id, status) per session — one bad backend won't spam
 * dozens of alerts during a busy hour.
 */
export function reportSubmissionError(formId, status, responseBody = '', extra = {}) {
  const key = `submission_err:${formId}:${status}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return report('submission_error', `Form "${formId}" failed: HTTP ${status}`, {
    form_id: formId,
    status,
    response_snippet: String(responseBody).slice(0, 200),
    ...extra,
  });
}

/**
 * Report when a search returns FEWER results than expected (≤ 2).
 *
 * Different from reportZeroResults — this catches city/service combos where
 * we technically have 1-2 garages but it's not enough inventory for a useful
 * page. These are real SEO opportunity signals: "people search this, we
 * have nothing to show, go scrape this segment".
 *
 * Throttled per query per session.
 */
export function reportLowInventory(query, resultCount, extra = {}) {
  if (resultCount > 2 || resultCount === 0) return; // 0 is reportZeroResults; >2 is fine
  const key = `low_inv:${query}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return report('low_inventory', `Only ${resultCount} result${resultCount === 1 ? '' : 's'}: "${query}"`, {
    query,
    result_count: resultCount,
    ...extra,
  });
}

export function reportLongTask(durationMs, extra = {}) {
  // Throttle per-session to avoid one bad page firing dozens of alerts.
  const key = `long_task:${window.location.pathname}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch { /* ignore */ }
  return report('long_task', `Long task: ${Math.round(durationMs)}ms on ${window.location.pathname}`, {
    duration_ms: Math.round(durationMs),
    page: window.location.pathname,
    ...extra,
  });
}

// Install global unhandled error + promise rejection handlers
export function installGlobalErrorHandlers() {
  // Stale-deploy chunk-load failures: the old index.html references chunk hashes
  // that no longer exist after a deploy → the browser 404s the chunk and the app
  // white-screens. Self-heal by reloading ONCE to fetch fresh HTML + chunks.
  // Guarded so it never loops. Complements ErrorBoundary, which only catches
  // render-phase errors — NOT async dynamic-import rejections (the ~90 "Unhandled
  // error" + "Failed to fetch dynamically imported module" we saw in debug_report).
  const maybeReloadOnChunkError = (msg) => {
    const m = (msg || '').toLowerCase();
    if (!(m.includes('loading chunk') || m.includes('chunkloaderror') ||
          m.includes('dynamically imported module') || m.includes('importing a module script failed'))) return false;
    if (!sessionStorage.getItem('vroum_chunk_reload')) {
      sessionStorage.setItem('vroum_chunk_reload', '1');
      window.location.reload();
    }
    return true;
  };

  window.addEventListener('error', (event) => {
    if (maybeReloadOnChunkError(event.message || event.error?.message)) return;
    // Broken <img> bubbles here with target.tagName === 'IMG' (no message).
    const t = event.target;
    if (t && t.tagName === 'IMG' && t.src) {
      // Ignore data: / blob: placeholders and the og-image fallback.
      if (/^(data:|blob:)/.test(t.src)) return;

      // Ignore Leaflet map tile CDN failures — transient network blips on
      // tile servers (CartoDB, OSM, etc.) that Leaflet handles by itself.
      // These create enormous noise and are not actionable.
      const TILE_HOSTS = [
        'basemaps.cartocdn.com',
        'tile.openstreetmap.org',
        'maps.googleapis.com',
        'mt0.google.com', 'mt1.google.com', 'mt2.google.com', 'mt3.google.com',
        'a.tile', 'b.tile', 'c.tile', // OSM subdomains
      ];
      if (TILE_HOSTS.some(h => t.src.includes(h))) return;

      // Ignore Google Places photo CDN — lh3.googleusercontent.com URLs from
      // the Places API have a short TTL and will naturally expire. GaragePhoto
      // already handles these with onError → category placeholder. Alerting
      // here creates noise without any actionable fix on our side.
      if (t.src.includes('lh3.googleusercontent.com') ||
          t.src.includes('lh4.googleusercontent.com') ||
          t.src.includes('lh5.googleusercontent.com') ||
          t.src.includes('lh6.googleusercontent.com')) return;

      reportBrokenImage(t.src, {
        alt: (t.alt || '').slice(0, 80),
        page: window.location.pathname,
      });
      return;
    }
    // Filter non-actionable noise that otherwise floods the alerts as empty {}
    // "crashes": cross-origin "Script error." (third-party scripts hide their
    // real message for CORS, leaving no filename/lineno to act on) and the
    // benign ResizeObserver loop notice. If there's truly no message AND no
    // location, there's nothing we could ever fix — drop it.
    const msg = event.message || '';
    const noDetail = !event.filename && !event.lineno;
    if (/^(script error|resizeobserver loop)/i.test(msg) || (!msg && noDetail)) return;
    reportError(msg || 'Unhandled error', {
      component: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  }, true /* capture — <img> error doesn't bubble */);

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason?.message || (typeof reason === 'string' ? reason : '') || '';
    if (maybeReloadOnChunkError(msg)) return;
    // Benign, non-actionable rejections: aborted requests (navigation cancels a
    // fetch), transient network blips, and empty rejections. These are not
    // frontend crashes and drown out the real ones.
    if (!msg || /abort|load failed|failed to fetch|networkerror|err_/i.test(msg)) return;
    reportError(msg, { type: 'unhandledrejection' });
  });

  // ── Long-task observer (main-thread blocks >500ms) ───────────────────────
  // PerformanceObserver 'longtask' entries fire for any task >50ms; we only
  // care about serious jank (>500ms — user-visibly frozen).
  try {
    if (typeof PerformanceObserver !== 'undefined') {
      const LONG_TASK_THRESHOLD = 500;
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > LONG_TASK_THRESHOLD) {
            reportLongTask(entry.duration, {
              name: entry.name,
              start: Math.round(entry.startTime),
            });
          }
        }
      });
      observer.observe({ entryTypes: ['longtask'] });
    }
  } catch { /* browser doesn't support longtask — silent */ }

  // ── Rage-click detector ───────────────────────────────────────────────────
  // 3+ clicks on the same element within 2s = frustration signal.
  let lastClick = { el: null, count: 0, t: 0 };
  const RAGE_WINDOW_MS = 2000;
  const RAGE_THRESHOLD = 3;
  window.addEventListener('click', (e) => {
    const el = e.target;
    if (!el) return;
    const now = Date.now();
    if (lastClick.el === el && now - lastClick.t < RAGE_WINDOW_MS) {
      lastClick.count += 1;
      if (lastClick.count === RAGE_THRESHOLD) {
        // Build a compact selector for the offending element.
        const tag = (el.tagName || 'UNK').toLowerCase();
        const id = el.id ? `#${el.id}` : '';
        const cls = (el.className && typeof el.className === 'string')
          ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
          : '';
        const txt = (el.innerText || el.textContent || '').trim().slice(0, 40);
        reportRageClick(`${tag}${id}${cls}`, RAGE_THRESHOLD, {
          text: txt,
          page: window.location.pathname,
        });
      }
    } else {
      lastClick = { el, count: 1, t: now };
    }
  }, true);
}
